<template>
  <div class="analytics-overview">
    <AnalyticsControls :type="AnalyticsType.OVERVIEW" />
    <div v-if="hasValue" class="analytics-overview__container">
      <div class="analytics-overview__block">
        <div class="analytics-overview__title">{{ KPIText[analyticsSelectedOption] }}</div>
        <AnalyticsOverviewViews class="analytics-overview__views" />
      </div>
      <div class="analytics-overview__block">
        <div class="analytics-overview__title">{{ ExtendsKPIText[analyticsSelectedOption] }}</div>
        <AnalyticsOverviewDoughnutChart
          class="analytics-overview__doughnut-chart"
          :dataDoughnutChart="doughnutChartData.chartData"
          :totalNumber="doughnutChartData.totalNumber"
          :totalPercent="doughnutChartData.totalPercent"
          :dataList="doughnutChartData.dataList"
        />
      </div>
    </div>
    <AnalyticsLineChart
      v-if="hasValue"
      class="analytics-overview__line-chart"
      :title="`${KPIText[analyticsSelectedOption]}`"
      subTitle="Отображение площадок на графике:"
      platformsTitle="Площадки"
      :type="AnalyticsType.OVERVIEW"
      :options="lineChartOptions"
      :lineDataChart="lineChartData"
      :platforms="lineChartPlatforms"
    />

    <AnalyticsPlaceholder
      v-if="!hasValue && !isFetchingAnalytics"
      class="analytics-overview__placeholder"
    >
      Нет данных за выбранный период
    </AnalyticsPlaceholder>
  </div>
</template>
<script>
import AnalyticsControls from '@/views/Analytics/AnalyticsControls';
import AnalyticsOverviewViews from '@/views/Analytics/AnalyticsOverview/AnalyticsOverviewViews';
import AnalyticsOverviewDoughnutChart from '@/views/Analytics/AnalyticsOverview/AnalyticsOverviewDoughnutChart';
import AnalyticsLineChart from '@/views/Analytics/AnalyticsLineChart';
import { getServiceColor, getServiceName, ServiceType } from '@/utils/services';
import {
  KPIText,
  ExtendsKPIText,
  KPIKeysBackendMapper,
  AnalyticsType,
  KPIKeys,
} from '@/utils/common';
import moment from 'moment';
import AnalyticsPlaceholder from '@/views/Analytics/AnalyticsPlaceholder';
import numberWithSpacesCeilRound from '@/mixin/numberWithSpaces';

export default {
  name: 'AnalyticsOverview',
  components: {
    AnalyticsControls,
    AnalyticsOverviewViews,
    AnalyticsOverviewDoughnutChart,
    AnalyticsLineChart,
    AnalyticsPlaceholder,
  },
  mixins: [numberWithSpacesCeilRound],
  data() {
    return {
      AnalyticsType,
      ServiceType,
      ExtendsKPIText,
      KPIText,
      colorsMap: new Map(),
      borderDashMap: new Map(),
    };
  },
  computed: {
    isFetchingAnalytics() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].isLoading;
    },
    currencyRates() {
      return this.$store.state.Auth.currencyRates;
    },
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    hasValue() {
      if (!this.overviewAnalyticsData) return false;
      return Object.keys(this.overviewAnalyticsData).some(
        (service) => this.overviewAnalyticsData[service].length
      );
    },
    analyticsSelectedOption() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].selectedOption;
    },
    overviewAnalyticsData() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].data;
    },
    doughnutChartData() {
      if (!this.overviewAnalyticsData) {
        return {
          chartData: {
            labels: [],
            datasets: [{ data: [], backgroundColor: [] }],
          },
          totalNumber: 0,
          totalPercent: null,
          dataList: [],
        };
      }

      const kpiKey = KPIKeysBackendMapper[this.analyticsSelectedOption];
      const labels = [];
      const data = [];
      const backgroundColor = [];
      const dataList = [];
      let totalPrevNumber = 0;

      // Проверяем, есть ли хотя бы одно НЕ-null значение в prev_*
      const hasPrevData = Object.values(this.overviewAnalyticsData).some((stats) =>
        stats.every((item) => item[`prev_${kpiKey}`])
      );

      // Собираем данные
      Object.entries(this.overviewAnalyticsData).forEach(([service, stats]) => {
        let count = 0;
        let prevCount = null;
        const color = this.getColorCached(service);

        count = stats.reduce((sum, item) => sum + Number(item[kpiKey]), 0);

        if (hasPrevData) {
          prevCount = stats.reduce((sum, item) => sum + Number(item[`prev_${kpiKey}`] || 0), 0);
        }

        if (count >= 0) {
          labels.push(getServiceName(service));
          data.push(this.numberWithSpacesCeilRound(count));
          backgroundColor.push(color);

          if (hasPrevData) {
            totalPrevNumber += prevCount;
          }

          dataList.push({
            service,
            count,
            prevCount,
            color,
            percent: null,
          });
        }
      });

      // Корректный расчёт totalNumber по всем сервисам с конвертацией валют
      let totalNumber = 0;
      Object.entries(this.overviewAnalyticsData).forEach(([service, stats]) => {
        const serviceTotal = stats.reduce((sum, item) => sum + Number(item[kpiKey]), 0);

        // Конвертируем валюту только для денежных показателей (расходы и средняя цена клика)
        if (
          this.analyticsSelectedOption === KPIKeys.EXPENSES ||
          this.analyticsSelectedOption === KPIKeys.AVERAGE_CPC
        ) {
          if (service === 'telegram') {
            totalNumber += serviceTotal * this.getExchangeRate('EUR', 'RUB');
          } else if (service === 'google') {
            totalNumber += serviceTotal * this.getExchangeRate('USD', 'RUB');
          } else {
            // Остальные сервисы уже в рублях
            totalNumber += serviceTotal;
          }
        } else {
          // Для неденежных показателей (показы, клики, конверсии, CTR) просто суммируем
          totalNumber += serviceTotal;
        }
      });

      // Если есть prev значения, вычисляем проценты
      if (hasPrevData) {
        const totalPercent =
          totalPrevNumber !== 0 ? ((totalNumber - totalPrevNumber) / totalPrevNumber) * 100 : 0;

        dataList.forEach((item) => {
          item.percent =
            item.prevCount !== 0 ? ((item.count - item.prevCount) / item.prevCount) * 100 : 0;
        });

        return {
          chartData: { labels, datasets: [{ data, backgroundColor }] },
          totalNumber,
          totalPercent,
          dataList,
        };
      }

      return {
        chartData: { labels, datasets: [{ data, backgroundColor }] },
        totalNumber,
        totalPercent: null,
        dataList: dataList.map((item) => ({ ...item, percent: null })),
      };
    },
    lineChartOptions() {
      return Object.keys(this.overviewAnalyticsData || {}).map((service) => ({
        label: getServiceName(service),
        value: service,
      }));
    },
    lineChartPlatforms() {
      return Object.keys(this.overviewAnalyticsData || {}).map((service) => {
        return {
          name: getServiceName(service),
          borderColor: this.getColorCached(service),
        };
      });
    },
    lineChartData() {
      const services = Object.keys(this.overviewAnalyticsData || {});
      const allDatesSet = new Set();
      services.forEach((service) => {
        (this.overviewAnalyticsData?.[service] || []).forEach((item) =>
          allDatesSet.add(item.for_date)
        );
      });
      const labels = Array.from(allDatesSet).sort();
      const kpiKey = KPIKeysBackendMapper[this.analyticsSelectedOption];

      const datasets = services.map((service) => {
        const stats = this.overviewAnalyticsData?.[service] || [];

        // Собираем значения по датам
        const dateToValues = {};

        stats.forEach((item) => {
          if (!dateToValues[item.for_date]) dateToValues[item.for_date] = [];
          dateToValues[item.for_date].push(Number(item[kpiKey]));
        });

        return {
          label: getServiceName(service),
          data: labels.map((date) => {
            const values = dateToValues[date] ? dateToValues[date].filter((v) => !isNaN(v)) : [];
            let result;
            result = values.length ? values.reduce((a, b) => a + b, 0) : 0;

            return Number(result.toFixed(2));
          }),
          borderColor: this.getColorCached(service),
          backgroundColor: 'transparent',
          borderWidth: 2,
          tension: 0.2,
          radius: 0,
        };
      });

      return {
        labels: labels.map((date) => moment(date).format('D MMM')),
        datasets,
      };
    },
  },
  methods: {
    getColorCached(key) {
      if (!this.colorsMap.has(key)) {
        this.colorsMap.set(key, getServiceColor(key));
      }
      return this.colorsMap.get(key);
    },
    getExchangeRate(from, to) {
      if (!this.currencyRates) return 1;
      const rate = this.currencyRates.find((el) => el.from === from && el.to === to);
      return rate ? rate.value : 1;
    },
  },
  mounted() {
    if (this.activeAccount && this.activeAccount.id && !this.currencyRates) {
      this.$store.dispatch('Auth/getCurrencyRates', { business_account_id: this.activeAccount.id });
    }
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.analytics-overview {
  &__placeholder {
    margin-top: 50px;
  }
  &__no-data-placeholder-text {
    color: $gray2;
    transition: opacity 0.4s;
  }
  &__line-chart {
    margin-top: 40px;
  }
  &__block {
    border-radius: 8px;
    background: $basicWhite;
    box-shadow: 0px 5px 10px 0px #9e9e9e1a;
    padding: 2.4rem;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  &__views {
    width: 100%;
    max-width: 376px;

    @include below(768px) {
      max-width: 100%;
    }
  }
  &__doughnut-chart {
    width: 100%;
    max-width: 450px;

    @include below(768px) {
      max-width: 100%;
    }
  }
  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 40px;
    margin-top: 20px;

    @include below(768px) {
      grid-template-columns: 1fr;
    }
  }
  &__title {
    font-weight: 700;
    font-size: 24px;
    color: $basicBlack;
    margin-bottom: 30px;
  }
}
</style>
